export function normalizeBudgetFolderKey(value) {
  return String(value || "")
    .trim()
    .replace(/^P-/i, "")
    .toUpperCase();
}

export function extractBudgetKeysFromImageHit(hit) {
  const rel = String(hit?.imageUrl || "")
    .replace(/^\/api\/nas\//, "")
    .replace(/^\/nas\//, "");
  const firstSegment = rel.split("/").filter(Boolean)[0];
  if (!firstSegment) return [];

  if (!/^P-/i.test(firstSegment) && !/^\d+$/.test(firstSegment)) {
    return [];
  }

  return [normalizeBudgetFolderKey(firstSegment)];
}

export function mapImageHitsToBudgetScores(hits) {
  const scores = {};
  for (const hit of hits || []) {
    for (const key of extractBudgetKeysFromImageHit(hit)) {
      if (!key) continue;
      const next = Number(hit?.score) || 0;
      scores[key] = Math.max(scores[key] ?? 0, next);
    }
  }
  return scores;
}

export function pickEstimateGridThumbnailFile(files) {
  const images = (files || []).filter(
    (f) => f.mimeType?.includes("image") && f.data,
  );
  if (!images.length) return null;

  return [...images].sort((a, b) => {
    const timeDiff = (b.modifiedAt || 0) - (a.modifiedAt || 0);
    if (timeDiff !== 0) return timeDiff;
    return String(b.fileName || "").localeCompare(String(a.fileName || ""));
  })[0];
}

export function base64Payload(dataUrl) {
  const raw = String(dataUrl || "");
  const idx = raw.indexOf(",");
  return idx >= 0 ? raw.slice(idx + 1) : raw;
}

export function findExactThumbnailMatches(queryBase64, thumbnailCandidates) {
  const queryPayload = base64Payload(queryBase64);
  if (!queryPayload) return {};

  const scores = {};
  for (const candidate of thumbnailCandidates || []) {
    const candidatePayload = base64Payload(candidate?.imageBase64);
    if (!candidatePayload || candidatePayload !== queryPayload) continue;
    const key = normalizeBudgetFolderKey(candidate?.numeroPresupuesto);
    if (key) scores[key] = 1;
  }
  return scores;
}

export function mergeEstimateImageHits(hitGroups) {
  const best = {};
  for (const hits of hitGroups || []) {
    for (const hit of hits || []) {
      const key = normalizeBudgetFolderKey(hit?.numeroPresupuesto);
      if (!key) continue;
      const score = Number(hit?.score) || 0;
      best[key] = Math.max(best[key] ?? 0, score);
    }
  }
  return Object.entries(best)
    .map(([numeroPresupuesto, score]) => ({ numeroPresupuesto, score }))
    .sort((a, b) => b.score - a.score);
}

export function mapEstimateImageHitsToBudgetScores(hits, minScore = 0.55) {
  const scores = {};
  for (const hit of hits || []) {
    const key = normalizeBudgetFolderKey(hit?.numeroPresupuesto);
    if (!key) continue;
    const next = Number(hit?.score) || 0;
    if (next < minScore) continue;
    scores[key] = Math.max(scores[key] ?? 0, next);
  }
  return scores;
}

export function filterStrongPhotoSearchScores(
  scores,
  { minScore = 0.55, minGapFromTop = 0.18, maxResults = 12 } = {},
) {
  const entries = Object.entries(scores || {}).filter(([, score]) => score >= minScore);
  if (!entries.length) return {};

  entries.sort((a, b) => b[1] - a[1]);
  const topScore = entries[0][1];
  const floor = Math.max(minScore, topScore - minGapFromTop);

  return Object.fromEntries(
    entries.filter(([, score]) => score >= floor).slice(0, maxResults),
  );
}

export function resolvePhotoSearchScores(hits, { maxResults = 12, minScore = 0.42 } = {}) {
  const raw = mapEstimateImageHitsToBudgetScores(hits, minScore);
  return Object.fromEntries(
    Object.entries(raw)
      .sort((a, b) => b[1] - a[1])
      .slice(0, maxResults),
  );
}

export function mergePhotoSearchScoreMaps(...maps) {
  const merged = {};
  for (const scores of maps) {
    for (const [key, value] of Object.entries(scores || {})) {
      merged[key] = Math.max(merged[key] ?? 0, Number(value) || 0);
    }
  }
  return merged;
}

export function getBudgetPhotoSearchScore(budget, scoreByKey) {
  if (!scoreByKey || !Object.keys(scoreByKey).length) return 0;
  const keys = [
    normalizeBudgetFolderKey(budget?.NumeroPresupuesto),
    normalizeBudgetFolderKey(budget?.IdPresupuesto),
  ];
  return Math.max(0, ...keys.map((k) => scoreByKey[k] || 0));
}

export function budgetMatchesPhotoSearch(budget, scoreByKey) {
  return getBudgetPhotoSearchScore(budget, scoreByKey) > 0;
}

export function fileToSearchBase64(file, maxWidth = 384, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read image"));
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });
}

export function fileToSearchBase64Resized(file, maxWidth = 384, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read image"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Invalid image"));
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const scale = Math.min(1, maxWidth / Math.max(img.width, 1));
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
